// Utils
import { ImMNElement, MNElement } from '../../../../common/elements/elementModelTypes';
import { getAutoHeight, getMaxHeight, getMinHeight } from '../../../../common/elements/utils/elementPropertyUtils';

interface CardDimensionProperties {
    minHeight?: number;
    maxHeight?: number;
}

export const getIsAutoHeight = (element: ImMNElement | MNElement): boolean =>
    !!getAutoHeight(element) || (!getMaxHeight(element) && !getMinHeight(element));

export const isHeightRestricted = (element: ImMNElement | MNElement): boolean =>
    !getAutoHeight(element) && !!getMaxHeight(element);

export const getCardDimensionPropertiesPx = ({
    element,
    gridSize,
}: {
    element: ImMNElement | MNElement;
    gridSize: number;
}): CardDimensionProperties => {
    // If min height return min-height
    const savedMinHeight = getMinHeight(element);
    if (savedMinHeight) return { minHeight: savedMinHeight * gridSize };

    const savedMaxHeight = getMaxHeight(element);
    if (savedMaxHeight) return { maxHeight: savedMaxHeight * gridSize };

    // If not overflowed and no min height or no max height, just size the card based on the contents
    return {};
};

// Calculate how far the text extends past its container. Return a negative number when the container is larger than the text.
export const getEditorOverflowDistance = (
    containerMaxHeight: number,
    editorHeight: number,
    paddingGu: number,
    gridSize: number,
): number => editorHeight - (containerMaxHeight - paddingGu * gridSize);

export const getIsEditorOverThreshold = (
    containerMaxHeight: number,
    editorHeight: number,
    paddingGu: number,
    gridSize: number,
): boolean => getEditorOverflowDistance(containerMaxHeight, editorHeight, gridSize, paddingGu) > 0;

type Props = {
    element: ImMNElement;
    gridSize: number;
    tempContainerHeight?: number;
};

type ContainerStyle = Partial<{
    minHeight: number;
    maxHeight: number;
    height: number;
}>;

/**
 * Determines the minHeight, maxHeight and height properties.
 */
export const resizableEditorContainerStyle = (
    props: Props,
    isEditingTargetContent: boolean,
): ContainerStyle | undefined => {
    const { element, gridSize, tempContainerHeight } = props;

    if (tempContainerHeight) {
        return {
            height: tempContainerHeight,
        };
    }

    // If auto height, then don't set the max or min height
    const isAutoHeight = getIsAutoHeight(element);
    if (isAutoHeight) return;

    const dimensionsProperties = getCardDimensionPropertiesPx({ element, gridSize });

    // If we're editing the card then don't set the max height
    const allowHeightRestriction = !isEditingTargetContent;

    return {
        ...dimensionsProperties,
        maxHeight: allowHeightRestriction ? dimensionsProperties.maxHeight : undefined,
    };
};
